/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';

/**
 * TypeScript interfaces for podcast management
 */

// User Info Structure
export interface UserInfo {
	userId: string;
	name?: string;
}

// Department Structure
export interface Department {
	name: string;
	count?: number;
}

// User Group Structure
export interface UserGroup {
	type: string;
	count?: number;
}

// Access Control Structure
export interface AccessControl {
	internalEmployees?: {
		allSelected?: boolean;
		departments?: Department[];
		specificUsers?: UserInfo[];
	};
	userGroups?: UserGroup[];
	participantCount?: number;
	participantLimit?: number;
}

// Cover Image Structure
export interface CoverImage {
	url: string;
	altText?: string;
	canEdit?: boolean;
	canDelete?: boolean;
}

// Episode Structure
export interface Episode {
	episodeId?: string; // Auto-generated, Read-only
	seasonId: string;
	title: string;
	description: string;
	audioUrl: string;
	duration: number;
	fileSize: number;
	fileType: 'mp3' | 'wav' | 'm4a' | 'ogg';
	episodeNumber: number;
	releaseDate: string;
	listens: number;
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	scheduledPublishDate?: string;
	createdAt: string;
	updatedAt: string;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	publishedAt?: string;
	allowDownload?: boolean;
	allowShare?: boolean;
	visibility?: 'public' | 'private';
	accessControl?: AccessControl;
}

// Season Structure
export interface Season {
	seasonId?: string; // Auto-generated, Read-only
	title: string;
	description?: string;
	coverImage?: CoverImage;
	episodeCount: number;
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	scheduledPublishDate?: string;
	createdAt: string;
	updatedAt: string;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	publishedAt?: string;
	episodes?: Episode[];
}

// Analytics Structure
export interface Analytics {
	totalListens: number;
	averageListeningTime: number;
	completionRate: number;
	topEpisodes: Array<{
		episodeId: string;
		title: string;
		listens: number;
	}>;
}

// Engagement Metrics
export interface EngagementMetrics {
	views: number;
	downloads: number;
	helpful: number;
	notHelpful: number;
	averageTimeSpent: number;
	listenCount: number;
	shareCount: number;
}

// Podcast Model
export interface Podcast {
	_id?: string; // Read-only
	companyId: string;
	companyName?: string;
	sourceType: 'system' | 'media_drive' | 'embed_link';
	title: string;
	description?: string;
	categoryIds: string[]; // Array of Category ObjectIds
	subCategory?: string;
	tags: string[];
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility: 'public' | 'private';
	coverImage?: CoverImage;
	seasons: Season[];
	episodes: Episode[];
	analytics: Analytics;
	engagement: EngagementMetrics;
	allowDownload: any;
	allowShare: any;
	accessControl?: AccessControl;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	createdAt: string;
	updatedAt: string;
	publishedAt?: string;
	scheduledPublishDate?: string;
}

export interface CreatePodcastPayload {
	companyId: string;
	companyName?: string;
	sourceType: 'system' | 'media_drive' | 'embed_link';
	title: string;
	description?: string;
	categoryIds?: string[];
	subCategory?: string;
	tags?: string[];
	status?: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility?: 'public' | 'private';
	coverImage?: CoverImage;
	seasons?: Season[];
	episodes?: Episode[];
	accessControl?: AccessControl;
	scheduledPublishDate?: string;
}

export interface PodcastListResponse {
	data: Podcast[];
	message: string;
	success: boolean;
	pagination: {
		total: number;
		offset: number;
		limit: number;
	};
}

// API endpoint name
const API_NAME = 'resourceManagementVPP';

/**
 * Get list of podcasts for a company
 * @param companyId - Company ID
 * @param params - Query parameters (pagination, filters, etc.)
 * @returns Promise with list of podcasts
 */
export const getPodcastsList = async (
	companyId: string,
	queryStringParameters?: {
		status?: string;
		category?: string;
		tag?: string;
		limit?: number;
		offset?: number;
		sortBy?: string;
		sortOrder?: 'asc' | 'desc';
		from?: 'MyResource' | 'Configuration';
	}
): Promise<PodcastListResponse> =>
{
	try
	{
		// let path = `/podcasts/company/${companyId}`;
		// const queryParams: string[] = [];

		// if (params?.status) queryParams.push(`status=${params.status}`);
		// if (params?.category) queryParams.push(`category=${params.category}`);
		// if (params?.tag) queryParams.push(`tag=${params.tag}`);
		// if (params?.limit) queryParams.push(`limit=${params.limit}`);
		// if (params?.offset) queryParams.push(`offset=${params.offset}`);
		// if (params?.sortBy) queryParams.push(`sortBy=${params.sortBy}`);
		// if (params?.sortOrder) queryParams.push(`sortOrder=${params.sortOrder}`);

		// if (queryParams.length > 0)
		// {
		// 	path += `?${queryParams.join('&')}`;
		// }

		const response = await API.get(API_NAME, `/podcasts/company/${companyId}`, { queryStringParameters });

		return response;
	}
	catch (error)
	{
		console.error('Error fetching podcasts:', error);
		toast.error('Failed to load podcasts');
		throw error;
	}
};

/**
 * Get a single podcast by ID
 * @param podcastId - Podcast ID
 * @returns Promise with podcast data
 */
export const getPodcastById = async (podcastId: string, queryStringParameters): Promise<Podcast> =>
{
	try
	{
		const response = await API.get(API_NAME, `/podcasts/${podcastId}`, { queryStringParameters });

		// Extract data from the API response format
		if (response.data && response.data.data)
		{
			return response.data.data;
		}

		return response.data;
	}
	catch (error)
	{
		console.error('Error fetching podcast by ID:', error);
		throw error;
	}
};

/**
 * Create a new podcast
 * @param payload - Podcast data payload
 * @returns Promise with created podcast data
 */
export const createPodcast = async (
	payload: CreatePodcastPayload
): Promise<Podcast> =>
{
	try
	{
		const response = await API.post(API_NAME, '/podcasts', {
			body : payload
		});

		// Extract data from the API response format
		if (response.data && response.data.data)
		{
			return response.data.data;
		}

		return response.data;
	}
	catch (error)
	{
		console.error('Error creating podcast:', error);
		toast.error('Failed to create podcast');
		throw error;
	}
};

/**
 * Add a new season to a podcast
 * @param podcastId - Podcast ID
 * @param seasonData - Season data
 * @returns Promise with updated podcast data including the new season
 */
export const addSeason = async (
	podcastId: string,
	seasonData: {
		title: string;
		description?: string;
		coverImage?: string;
	}
): Promise<Podcast> =>
{
	try
	{
		const response = await API.post(API_NAME, `/podcasts/${podcastId}/seasons`, {
			body : seasonData
		});

		// Extract data from the API response format
		if (response.data && response.data.data)
		{
			return response.data.data;
		}

		return response.data;
	}
	catch (error)
	{
		console.error('Error adding season to podcast:', error);
		toast.error('Failed to add season');
		throw error;
	}
};

/**
 * Add a new episode to a season
 * @param podcastId - Podcast ID
 * @param seasonId - Season ID
 * @param episodeData - Episode data
 * @returns Promise with updated podcast data including the new episode
 */
export const addEpisode = async (
	podcastId: string,
	seasonId: string,
	episodeData: {
		title: string;
		description?: string;
		audioUrl: string;
		duration?: number;
		fileSize?: number;
		fileType: 'mp3' | 'wav' | 'm4a' | 'ogg';
		episodeNumber: number;
		releaseDate?: string;
		allowDownload?: boolean;
		allowShare?: boolean;
		visibility?: 'public' | 'private';
		accessControl?: AccessControl;
	}
): Promise<Podcast> =>
{
	try
	{
		const response = await API.post(API_NAME, `/podcasts/${podcastId}/seasons/${seasonId}/episodes`, {
			body : episodeData
		});

		// Extract data from the API response format
		if (response.data && response.data.data)
		{
			return response.data.data;
		}

		return response.data;
	}
	catch (error)
	{
		console.error('Error adding episode to season:', error);
		toast.error('Failed to add episode');
		throw error;
	}
};

/**
 * Helper function to organize episodes by seasons for the UI
 * @param podcast - Podcast object with seasons and episodes
 * @returns Podcast with episodes organized by seasons
 */
export const organizeEpisodesBySeasons = (podcast: Podcast): Podcast =>
{
	// Create a copy to avoid mutating the original
	const organizedPodcast = { ...podcast };

	// Skip if no seasons or episodes
	if (!podcast.seasons || !podcast.episodes || podcast.episodes.length === 0)
	{
		return organizedPodcast;
	}

	// Create a map of seasons for quicker access
	const seasonsMap = {} as Record<string, Season>;

	// Initialize seasons with empty episodes arrays
	organizedPodcast.seasons = podcast.seasons.map((season) =>
	{
		const newSeason = { ...season, episodes: [] as Episode[] };

		seasonsMap[season.seasonId || ''] = newSeason;

		return newSeason;
	});

	// Assign episodes to their respective seasons
	podcast.episodes.forEach((episode) =>
	{
		if (episode.seasonId && seasonsMap[episode.seasonId])
		{
			seasonsMap[episode.seasonId].episodes = seasonsMap[episode.seasonId].episodes || [];
			seasonsMap[episode.seasonId].episodes?.push(episode);
		}
	});

	return organizedPodcast;
};

/**
 * Update a podcast (partial update)
 * @param podcastId - Podcast ID
 * @param updateData - Partial podcast data to update
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updatePodcast = async (
	podcastId: string,
	updateData: any,
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId)
		{
			throw new Error('No podcast ID provided');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}`, {
			body : updateData
		});

		console.log('response', response);

		if (showToast)
		{
			toast.success('Podcast updated successfully');
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating podcast ${podcastId}:`, error);
		if (showToast)
		{
			// More specific error messages based on the error type
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this podcast');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Podcast not found');
			}
			else
			{
				toast.error('Failed to update podcast');
			}
		}
		throw error;
	}
};

/**
 * Update a season's details
 * @param podcastId - Parent podcast ID
 * @param seasonId - Season ID to update
 * @param updateData - Season update data
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updateSeason = async (
	podcastId: string,
	seasonId: string,
	updateData: {
		title?: string;
		description?: string;
		coverImage?: {
			url: string;
			altText?: string;
			canEdit?: boolean;
			canDelete?: boolean;
		};
	},
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId || !seasonId)
		{
			throw new Error('Both podcast ID and season ID are required');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/seasons/${seasonId}`, {
			body : updateData
		});

		if (showToast)
		{
			toast.success('Season updated successfully');
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating season ${seasonId}:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this season');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Season not found');
			}
			else
			{
				toast.error('Failed to update season');
			}
		}
		throw error;
	}
};

/**
 * Update an episode's details
 * @param podcastId - Parent podcast ID
 * @param episodeId - Episode ID to update
 * @param updateData - Episode update data
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updateEpisode = async (
	podcastId: string,
	episodeId: string,
	updateData: {
		title?: string;
		description?: string;
		coverImage?: {
			url: string;
			altText?: string;
			canEdit?: boolean;
			canDelete?: boolean;
		};
	},
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId || !episodeId)
		{
			throw new Error('Both podcast ID and episode ID are required');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/episodes/${episodeId}`, {
			body : updateData
		});

		if (showToast)
		{
			toast.success('Episode updated successfully');
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating episode ${episodeId}:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this episode');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Episode not found');
			}
			else
			{
				toast.error('Failed to update episode');
			}
		}
		throw error;
	}
};

/**
 * Delete a podcast by ID
 * @param podcastId - Podcast ID to delete
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with deletion result
 */
export const deletePodcast = async (
	podcastId: string,
	showToast: boolean = true
): Promise<any> =>
{
	try
	{
		if (!podcastId)
		{
			throw new Error('No podcast ID provided');
		}

		const response = await API.del(API_NAME, `/podcasts/${podcastId}`, {});

		if (showToast)
		{
			toast.success('Podcast deleted successfully');
		}

		return response;
	}
	catch (error)
	{
		console.error(`Error deleting podcast ${podcastId}:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to delete this podcast');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Podcast not found');
			}
			else
			{
				toast.error('Failed to delete podcast');
			}
		}
		throw error;
	}
};

/**
 * Delete a season by ID
 * @param podcastId - Parent podcast ID
 * @param seasonId - Season ID to delete
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with deletion result
 */
export const deleteSeason = async (
	podcastId: string,
	seasonId: string,
	showToast: boolean = true
): Promise<any> =>
{
	try
	{
		if (!podcastId || !seasonId)
		{
			throw new Error('Both podcast ID and season ID are required');
		}

		const response = await API.del(API_NAME, `/podcasts/${podcastId}/seasons/${seasonId}`, {});

		if (showToast)
		{
			toast.success('Season deleted successfully');
		}

		return response;
	}
	catch (error)
	{
		console.error(`Error deleting season ${seasonId} from podcast ${podcastId}:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to delete this season');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Season not found');
			}
			else if ((error as any)?.response?.status === 400)
			{
				toast.error('Cannot delete season with episodes. Remove episodes first.');
			}
			else
			{
				toast.error('Failed to delete season');
			}
		}
		throw error;
	}
};

/**
 * Delete an episode by ID
 * @param podcastId - Parent podcast ID
 * @param seasonId - Parent season ID
 * @param episodeId - Episode ID to delete
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with deletion result
 */
export const deleteEpisode = async (
	podcastId: string,
	episodeId: string,
	showToast: boolean = true
): Promise<any> =>
{
	try
	{
		if (!podcastId || !episodeId)
		{
			throw new Error('Podcast ID, season ID, and episode ID are all required');
		}

		const response = await API.del(API_NAME, `/podcasts/${podcastId}/episodes/${episodeId}`, {});

		if (showToast)
		{
			toast.success('Episode deleted successfully');
		}

		return response;
	}
	catch (error)
	{
		console.error(`Error deleting episode ${episodeId} from podcast ${podcastId}, `, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to delete this episode');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Episode not found');
			}
			else
			{
				toast.error('Failed to delete episode');
			}
		}
		throw error;
	}
};

/**
 * Get episodes by season ID
 * @param podcastId - Podcast ID
 * @param seasonId - Season ID
 * @param limit - Number of items per page
 * @param offset - Offset for pagination
 * @returns Promise with episodes data
 */
export const getEpisodesBySeason = async (
	podcastId: string,
	seasonId: string,
	limit: number,
	offset: number
): Promise<any> =>
{
	try
	{
		// Construct path with query parameters
		const path = `/podcasts/${podcastId}/seasons/${seasonId}/episodes?limit=${limit}&offset=${offset}`;

		const response = await API.get(API_NAME, path, {});

		return response;
	}
	catch (error)
	{
		console.error('Error fetching episodes by season:', error);
		toast.error('Failed to load episodes');
		throw error;
	}
};

/**
 * Update a podcast's publication status
 * @param podcastId - Podcast ID
 * @param statusData - Status update data
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updatePodcastStatus = async (
	podcastId: string,
	statusData: {
		status: 'draft' | 'published' | 'scheduled' | 'unpublished';
		scheduledPublishDate?: number;
	},
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId)
		{
			throw new Error('Podcast ID is required');
		}

		if (!statusData.status)
		{
			throw new Error('Status is required');
		}

		if (statusData.status === 'scheduled' && !statusData.scheduledPublishDate)
		{
			throw new Error('Scheduled publish date is required for scheduled status');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/status`, {
			body : statusData
		});

		if (showToast)
		{
			toast.success(`Podcast ${statusData.status} successfully`);
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating podcast ${podcastId} status:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this podcast');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Podcast not found');
			}
			else
			{
				toast.error(`Failed to update podcast status to ${statusData.status}`);
			}
		}
		throw error;
	}
};

/**
 * Update a season's publication status
 * @param podcastId - Podcast ID
 * @param seasonId - Season ID
 * @param statusData - Status update data
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updatePodcastSeasonStatus = async (
	podcastId: string,
	seasonId: string,
	statusData: {
		status: 'draft' | 'published' | 'scheduled' | 'unpublished';
		scheduledPublishDate?: number;
	},
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId || !seasonId)
		{
			throw new Error('Both podcast ID and season ID are required');
		}

		if (!statusData.status)
		{
			throw new Error('Status is required');
		}

		if (statusData.status === 'scheduled' && !statusData.scheduledPublishDate)
		{
			throw new Error('Scheduled publish date is required for scheduled status');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/seasons/${seasonId}/status`, {
			body : statusData
		});

		if (showToast)
		{
			toast.success(`Season ${statusData.status} successfully`);
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating season ${seasonId} status:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this season');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Season not found');
			}
			else
			{
				toast.error(`Failed to update season status to ${statusData.status}`);
			}
		}
		throw error;
	}
};

/**
 * Update an episode's publication status
 * @param podcastId - Podcast ID
 * @param seasonId - Season ID
 * @param episodeId - Episode ID
 * @param statusData - Status update data
 * @param showToast - Whether to show toast messages (default: true)
 * @returns Promise with updated podcast data
 */
export const updatePodcastSeasonEpisodeStatus = async (
	podcastId: string,
	episodeId: string,
	statusData: {
		status: 'draft' | 'published' | 'scheduled' | 'unpublished';
		scheduledPublishDate?: number;
	},
	showToast: boolean = true
): Promise<Podcast> =>
{
	try
	{
		if (!podcastId || !episodeId)
		{
			throw new Error('Podcast ID, and episode ID are all required');
		}

		if (!statusData.status)
		{
			throw new Error('Status is required');
		}

		if (statusData.status === 'scheduled' && !statusData.scheduledPublishDate)
		{
			throw new Error('Scheduled publish date is required for scheduled status');
		}

		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/episodes/${episodeId}/status`, {
			body : statusData
		});

		if (showToast)
		{
			toast.success(`Episode ${statusData.status} successfully`);
		}

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating episode ${episodeId} status:`, error);
		if (showToast)
		{
			if ((error as any)?.response?.status === 403)
			{
				toast.error('You do not have permission to update this episode');
			}
			else if ((error as any)?.response?.status === 404)
			{
				toast.error('Episode not found');
			}
			else
			{
				toast.error(`Failed to update episode status to ${statusData.status}`);
			}
		}
		throw error;
	}
};

export const updatePodcastEngagement = async (
	podcastId: string,
	metric: string,
	value: number = 1
): Promise<Podcast> =>
{
	try
	{
		const response = await API.patch(API_NAME, `/podcasts/${podcastId}/engagement`, {
			body : {
				metric,
				value
			}
		});

		return response.data;
	}
	catch (error)
	{
		console.error(`Error updating podcast engagement for ${podcastId}:`, error);
		throw error;
	}
};

/**
 * Toggle favorite status for a podcast
 * @param podcastId - Podcast ID
 * @returns Promise with favorite status and updated podcast
 */
export const togglePodcastFavorite = async (
	podcastId: string
): Promise<{ success: boolean; data: { podcast: any; isFavorited: boolean; favoriteCount: number }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/podcasts/${podcastId}/favorites`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error toggling podcast favorite:', error);
		throw error;
	}
};

export const updatePodcastListens = async (
	podcastId: string,
	episodeId: string
): Promise<any> =>
{
	try
	{
		const response = await API.post(API_NAME, `/podcasts/${podcastId}/episodes/${episodeId}/listens`, {});

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating podcast listens:', error);
		throw error;
	}
};

export const updateAudioProgress = async (
	podcastId: string,
	watchData: {
		episodeId: string;
		seasonId: string;
		currentTime: number;
		duration: number;
	},
	deviceInfo: {
		deviceType: string;
		browser: string;
		os: string;
		ipAddress: string;
	}
) =>
{
	return await API.put(API_NAME, `/watch-history/podcast/${podcastId}/progress`, {
		body : {
			watchData,
			deviceInfo
		}
	});
};